import React, { useState } from 'react';
import { Shield, Key, FileText, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Textarea } from '@vaiu/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vaiu/ui/tabs';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { useEthereum } from '@/context/EthereumContext';

const EthereumWalletCreation: React.FC = () => {
  const { createWallet, importWallet, loading, error } = useEthereum();
  const [importMethod, setImportMethod] = useState<'create' | 'import'>('create');
  const [privateKey, setPrivateKey] = useState('');
  const [mnemonic, setMnemonic] = useState('');
  const [importing, setImporting] = useState(false);

  const handleCreateWallet = async () => {
    try {
      await createWallet();
    } catch (error) {
      console.error('Failed to create wallet:', error);
    }
  };

  const handleImportWallet = async () => {
    if (!privateKey && !mnemonic) {
      return;
    }

    setImporting(true);
    try {
      await importWallet({
        privateKey: privateKey || undefined,
        mnemonic: mnemonic || undefined,
      });
    } catch (error) {
      console.error('Failed to import wallet:', error);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Set Up Your Ethereum Wallet</h2>
        <p className="text-muted-foreground">
          You need an Ethereum wallet to deposit and withdraw ETH
        </p>
      </div>

      <Tabs value={importMethod} onValueChange={(v) => setImportMethod(v as 'create' | 'import')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="create">Create New Wallet</TabsTrigger>
          <TabsTrigger value="import">Import Existing</TabsTrigger>
        </TabsList>

        <TabsContent value="create">
          <Card>
            <CardHeader>
              <CardTitle>
                <Shield className="h-5 w-5 inline mr-2" />
                Create a New Wallet
              </CardTitle>
              <CardDescription>
                We'll generate a secure Ethereum wallet for you
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium">What you'll get:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Secure HD wallet (BIP39/BIP44 standard)</li>
                  <li>Encrypted private keys</li>
                  <li>Automatic backup</li>
                  <li>Multi-network support</li>
                </ul>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Your wallet will be securely encrypted and stored. Never share your private keys with anyone.
                </AlertDescription>
              </Alert>

              <Button
                className="w-full"
                size="lg"
                onClick={handleCreateWallet}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating Wallet...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Create Wallet
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="import">
          <Card>
            <CardHeader>
              <CardTitle>
                <Key className="h-5 w-5 inline mr-2" />
                Import Your Wallet
              </CardTitle>
              <CardDescription>
                Import an existing Ethereum wallet using your private key or seed phrase
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="private-key">Private Key</Label>
                <Input
                  id="private-key"
                  type="password"
                  placeholder="Enter your private key (0x...)"
                  value={privateKey}
                  onChange={(e) => setPrivateKey(e.target.value)}
                  disabled={importing}
                />
              </div>

              <div className="text-center text-sm text-muted-foreground">OR</div>

              <div className="space-y-2">
                <Label htmlFor="mnemonic">Mnemonic Phrase (12 or 24 words)</Label>
                <Textarea
                  id="mnemonic"
                  placeholder="Enter your seed phrase words separated by spaces"
                  rows={3}
                  value={mnemonic}
                  onChange={(e) => setMnemonic(e.target.value)}
                  disabled={importing}
                />
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Your private key will be encrypted and stored securely. We never have access to your unencrypted keys.
                </AlertDescription>
              </Alert>

              <Button
                className="w-full"
                size="lg"
                onClick={handleImportWallet}
                disabled={importing || (!privateKey && !mnemonic)}
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing Wallet...
                  </>
                ) : (
                  <>
                    <Key className="h-4 w-4 mr-2" />
                    Import Wallet
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default EthereumWalletCreation;
